import { call, customEvent } from "goi_common";

import { GOI_PHONE } from "@/constants/contact";
import { ParlorHeader } from "@/containers/ParlorDetailContainer/components/ParlorHeader";
import { RegionFacilitiesData, RegionParlor } from "@/utils/regionFacilities";
import { getRegionFaqs } from "@/utils/getRegionFaqs";

import { RegionHero } from "./components/RegionHero";
import { RegionSectionTabs, RegionTab } from "./components/RegionSectionTabs";
import { RegionParlorList } from "./components/RegionParlorList";
import { RegionCrematoria } from "./components/RegionCrematoria";
import { RegionBurials } from "./components/RegionBurials";
import { RegionTotalCostCTA } from "./components/RegionTotalCostCTA";
import { RegionFAQ } from "./components/RegionFAQ";
import { RegionDisclaimer, RegionNearby } from "./components/RegionNearby";
import { RegionFixedBar } from "./components/RegionFixedBar";
import { useChuseokOff } from "@/hooks/useChuseokOff";

const UTM = "utm_source=parlor_goi&utm_campaign=region";

interface RegionParlorsContainerProps extends RegionFacilitiesData {
  /**
   * 이 페이지가 주로 소개하는 대상. 섹션과 탭 순서만 바뀌고 데이터는 같다.
   * /region/parlor/* 는 "parlor", /region/facility/* 는 "facility",
   * /region/crematorium/* 는 "crematorium".
   */
  focus?: "parlor" | "facility" | "crematorium";
}

export const RegionParlorsContainer = ({
  focus = "parlor",
  ...data
}: RegionParlorsContainerProps) => {
  const {
    sidoName,
    sigunguName,
    parlors,
    publicCount,
    privateCount,
    binsoMinCharge,
    binsoMaxCharge,
    familyCostMin,
    familyCostMax,
    crematoria,
    crematoriaInRegion,
    insideCrematorium,
    burials,
    nearbyRegions,
    sigunguOptions,
    isInsideTierKnown,
    hasParlorInRegion,
  } = data;

  const faqs = getRegionFaqs(data);

  const parlorTab: RegionTab = {
    id: "parlors",
    label: "장례식장",
    count: parlors.length,
    trigger: "nav_parlor",
  };
  const crematoriumTabs: RegionTab[] = crematoria.length
    ? [
        {
          id: "crematorium",
          label: "인근 화장장",
          count: crematoria.length,
          trigger: "nav_crematorium",
        },
      ]
    : [];
  const burialTabs: RegionTab[] = burials.length
    ? [
        {
          id: "burial",
          label: "장지",
          count: burials.length,
          trigger: "nav_burial",
        },
      ]
    : [];
  const faqTabs: RegionTab[] = faqs.length
    ? [{ id: "faq", label: "자주 묻는 질문", trigger: "nav_faq" }]
    : [];

  // 주로 소개하는 대상을 앞에 둔다
  const tabs: RegionTab[] =
    focus === "crematorium"
      ? [...crematoriumTabs, parlorTab, ...burialTabs, ...faqTabs]
      : focus === "facility"
      ? [...burialTabs, parlorTab, ...crematoriumTabs, ...faqTabs]
      : [parlorTab, ...crematoriumTabs, ...burialTabs, ...faqTabs];

  // focus 에 따라 위치만 바뀌고 내용은 같다. 두 자리에 같은 트리를 쓰지 않게 뽑아둔다
  const crematoriaSection = (
    <RegionCrematoria
      sigunguName={sigunguName}
      crematoria={crematoria}
      isInsideTierKnown={isInsideTierKnown}
      isFocused={focus === "crematorium"}
      moreHref={
        sigunguOptions.length > 0
          ? `/region/crematorium/${encodeURIComponent(sidoName)}/`
          : `/region/crematorium/${encodeURIComponent(
              sidoName
            )}/${encodeURIComponent(sigunguName)}/`
      }
    />
  );

  const burialSection = (
    <RegionBurials
      sigunguName={sigunguName}
      burials={burials}
      footer={<RegionNearby kind="facility" nearbyRegions={nearbyRegions} />}
    />
  );

  const totalCostHref = `${process.env.NEXT_PUBLIC_PLATFORM_WEB_URL}/match/request/?${UTM}&utm_content=total_cost`;

  /**
   * 2026 추석. 이 컨테이너를 장례식장, 장지, 화장장 지역 페이지가 같이 쓴다.
   * 이번 차단 대상은 장지, 화장장이라 focus 로 갈라서 장례식장 지역은 건드리지 않는다.
   */
  const isCallOff = useChuseokOff() && focus !== "parlor";

  const buildQuoteHref = (parlor: RegionParlor) =>
    `${process.env.NEXT_PUBLIC_PLATFORM_WEB_URL}/match/parlor-requests/${parlor.id}/?${UTM}&utm_content=parlor_quote`;

  return (
    <div className="rg-page">
      <ParlorHeader
        hidePhoneStrip
        showPhoneCta={!isCallOff}
        onPhoneCtaClick={() => {
          customEvent({
            newGtm: { path: "prlr_region", trigger_and_target: "btn_call" },
          });
          call(GOI_PHONE);
        }}
      />

      <main>
        <RegionHero
          focus={focus}
          burialCount={burials.length}
          crematoriaCount={crematoria.length}
          crematoriaInRegion={crematoriaInRegion}
          facilityHref={`${process.env.NEXT_PUBLIC_PLATFORM_WEB_URL}/facilities/search/?${UTM}&utm_content=hero_facility`}
          sidoName={sidoName}
          sigunguName={sigunguName}
          hasParlorInRegion={hasParlorInRegion}
          parlorCount={parlors.length}
          publicCount={publicCount}
          privateCount={privateCount}
          binsoMinCharge={binsoMinCharge}
          binsoMaxCharge={binsoMaxCharge}
          familyCostMin={familyCostMin}
          familyCostMax={familyCostMax}
          insideCrematorium={insideCrematorium}
          isSido={sigunguOptions.length > 0}
          regionCount={sigunguOptions.length}
          totalCostHref={`${process.env.NEXT_PUBLIC_PLATFORM_WEB_URL}/match/request/?${UTM}&utm_content=hero_cost`}
        />

        <RegionSectionTabs tabs={tabs} />

        <div className="rg-body">
          {focus === "crematorium" && crematoriaSection}
          {focus === "facility" && burialSection}

          <RegionParlorList
            sigunguName={sigunguName}
            hasParlorInRegion={hasParlorInRegion}
            parlors={parlors}
            sigunguOptions={sigunguOptions}
            buildQuoteHref={buildQuoteHref}
            footer={
              <RegionNearby kind="parlor" nearbyRegions={nearbyRegions} />
            }
          />

          {focus !== "crematorium" && crematoriaSection}
          {focus !== "facility" && burialSection}

          <RegionTotalCostCTA
            hideCall={isCallOff}
            sigunguName={sigunguName}
            familyCostMin={familyCostMin}
            familyCostMax={familyCostMax}
            insideCrematorium={insideCrematorium}
            burialMinCharge={burials[0]?.minCharge ?? null}
            href={totalCostHref}
          />

          <RegionFAQ sigunguName={sigunguName} faqs={faqs} />

          <RegionDisclaimer />
        </div>
      </main>

      <RegionFixedBar
        hideCall={isCallOff}
        sigunguName={sigunguName}
        familyCostMin={familyCostMin}
        familyCostMax={familyCostMax}
        href={`${process.env.NEXT_PUBLIC_PLATFORM_WEB_URL}/match/request/?${UTM}&utm_content=fixed_cost`}
      />
    </div>
  );
};
